/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './SecondaryButton.style';

import BaseButton from 'src/shared/ui/component/button/BaseButton';
import { hex2rgb } from 'src/shared/core/util/colorUtils';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';

import type { Button } from '@e180/ui/lib/component/button/type/buttonTypes';

type Props = Button & {
  layoutClass?: string,
  fitText: ?boolean,
  stopPropagation: ?boolean,
  withSuccess: ?boolean,
  withError: ?boolean,
  children: ?string,
};

const useStyles = createUseStyles(styles);

const SecondaryButton = ({
  layoutClass,
  href,
  type,
  handleClick,
  handleMouseOver,
  isDisabled,
  isSubmitting,
  isFetching,
  large,
  fitText,
  stopPropagation,
  withSuccess,
  withError,
  hasFocus,
  children,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  const { r, g, b } = hex2rgb(accentColor) || {};

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor, r, g, b });

  const rootClasses = classNames(classes.root, layoutClass, {
    [classes['root-withError']]: withError,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <BaseButton
      className={rootClasses}
      layoutClass
      href={href}
      type={type}
      handleClick={handleClick}
      handleMouseOver={handleMouseOver}
      isDisabled={isDisabled}
      isSubmitting={isSubmitting}
      isFetching={isFetching}
      large={large}
      fitText={fitText}
      stopPropagation={stopPropagation}
      withSuccess={withSuccess}
      hasFocus={hasFocus}
    >
      {children}
    </BaseButton>
  );
};

export default compose(React.memo)(SecondaryButton);
